import * as v2 from '@/core/services/api/v2';

/**
 * Funcionários do negócio, falando com o backend V2.
 *
 * No V2 o funcionário só equipa VARA e ISCA (barco não existe mais para
 * funcionário); a vara é referida pelo id de CATÁLOGO e a isca sai de verdade
 * do estoque do jogador. Toda validação de posse/quantidade é do servidor.
 */

/** Mensagem de erro no padrão do V2 (ver comentário em business.js). */
function mensagemDeErro(error) {
  return (
    error.response?.data?.message ||
    error.response?.data?.error?.message ||
    error.message
  );
}

/**
 * O equipamento que o V2 devolve ({rod, bait}) no shape que os componentes
 * leem ({rod, boat, bait}) — `boat` fica null porque funcionário não tem barco.
 */
function equipamentoParaStore(equipamento) {
  return {
    rod: equipamento?.rod || null,
    boat: null,
    bait: equipamento?.bait || null
  };
}

const state = {
  employees: [],
  selectedEmployee: null,
  employeeEquipment: {},
  employeeUpgrades: {},
  // Custos de upgrade por funcionário, cacheados da MESMA chamada que traz os
  // níveis — o custo é do servidor, o cliente só exibe.
  employeeUpgradeCosts: {}
};

const mutations = {
  SET_EMPLOYEES(state, employees) {
    state.employees = employees;
  },
  SET_SELECTED_EMPLOYEE(state, employee) {
    state.selectedEmployee = employee;
  },
  SET_EMPLOYEE_EQUIPMENT(state, { employeeId, equipment }) {
    state.employeeEquipment = {
      ...state.employeeEquipment,
      [employeeId]: equipment
    };
  },
  UPDATE_EMPLOYEE(state, updatedEmployee) {
    const index = state.employees.findIndex(e => e.id === updatedEmployee.id);
    if (index !== -1) {
      state.employees.splice(index, 1, updatedEmployee);
    }

    if (state.selectedEmployee && state.selectedEmployee.id === updatedEmployee.id) {
      state.selectedEmployee = updatedEmployee;
    }
  },
  SET_EMPLOYEE_UPGRADES(state, { employeeId, upgrades }) {
    state.employeeUpgrades = {
      ...state.employeeUpgrades,
      [employeeId]: upgrades
    };
  },
  UPDATE_EMPLOYEE_UPGRADES(state, { employeeId, upgrades }) {
    if (state.employeeUpgrades[employeeId]) {
      state.employeeUpgrades[employeeId] = {
        ...state.employeeUpgrades[employeeId],
        ...upgrades
      };
    } else {
      state.employeeUpgrades[employeeId] = upgrades;
    }
  },
  SET_EMPLOYEE_UPGRADE_COSTS(state, { employeeId, costs }) {
    state.employeeUpgradeCosts = {
      ...state.employeeUpgradeCosts,
      [employeeId]: costs
    };
  },
  UPDATE_EMPLOYEE_UPGRADE_COST(state, { employeeId, stat, cost }) {
    state.employeeUpgradeCosts = {
      ...state.employeeUpgradeCosts,
      [employeeId]: {
        ...(state.employeeUpgradeCosts[employeeId] || {}),
        [stat]: cost
      }
    };
  }
};

const actions = {
  // Carrega todos os funcionários do jogador
  async loadEmployees({ commit }, playerId) {
    try {
      // A rota do negócio traz os funcionários já no shape do cliente
      const result = await v2.negocio(playerId);
      if (result && result.employees && result.employees.employees) {
        commit('SET_EMPLOYEES', result.employees.employees);
      }
      return result;
    } catch (error) {
      console.error('Error loading employees:', error);
      throw new Error(mensagemDeErro(error));
    }
  },

  // Seleciona um funcionário para gerenciar
  selectEmployee({ commit }, employee) {
    commit('SET_SELECTED_EMPLOYEE', employee);
  },

  // Equipamento (vara + isca) de um funcionário
  async getEmployeeEquipment({ commit, rootState }, employeeId) {
    try {
      const playerId = rootState.player?.id;
      const resultado = await v2.equipamentoDoFuncionario(playerId, employeeId);
      const equipment = equipamentoParaStore(resultado);
      commit('SET_EMPLOYEE_EQUIPMENT', { employeeId, equipment });
      return { equipment };
    } catch (error) {
      console.error('Error getting employee equipment:', error);
      throw new Error(mensagemDeErro(error));
    }
  },

  /**
   * Equipa um item no funcionário. Só vara no V2 — quem valida se o jogador
   * possui a vara (e se ela está livre) é o servidor; as checagens locais do
   * contrato antigo saíram porque trabalhavam com o shape do Strapi.
   */
  async equipItem({ commit, rootState }, { employeeId, category, item }) {
    try {
      const playerId = rootState.player?.id;

      if (category !== 'rods') {
        // Barco não se equipa em funcionário no V2; isca tem fluxo próprio
        // (addBait, com quantidade). Falha alto para não silenciar um bug.
        throw new Error('No V2 o funcionário só equipa vara (isca vai por addBait).');
      }

      // `rodId` é o id de CATÁLOGO: nos itens do inventário do jogador ele é o
      // `baseId`; o `id` puro só serve quando o item já veio do catálogo.
      const equipamento = await v2.definirVaraDoFuncionario(
        playerId,
        employeeId,
        item.baseId || item.id
      );
      const equipment = equipamentoParaStore(equipamento);
      commit('SET_EMPLOYEE_EQUIPMENT', { employeeId, equipment });

      // Shape que os chamadores antigos esperam
      return { success: true, employee: { id: employeeId, equipment } };
    } catch (error) {
      console.error('Error equipping item for employee:', error);
      throw new Error(mensagemDeErro(error));
    }
  },

  // Desequipa: vara volta a null; isca volta ao estoque do jogador
  async unequipItem({ commit, rootState }, { employeeId, category }) {
    try {
      const playerId = rootState.player?.id;

      let equipamento;
      if (category === 'baits') {
        // Retirar a isca devolve a quantidade restante ao estoque do jogador
        equipamento = await v2.retirarIsca(playerId, employeeId);
      } else {
        // `null` desequipa a vara
        equipamento = await v2.definirVaraDoFuncionario(playerId, employeeId, null);
      }

      const equipment = equipamentoParaStore(equipamento);
      commit('SET_EMPLOYEE_EQUIPMENT', { employeeId, equipment });

      return { success: true, employee: { id: employeeId, equipment } };
    } catch (error) {
      console.error('Error unequipping item from employee:', error);
      throw new Error(mensagemDeErro(error));
    }
  },

  /**
   * Entrega isca ao funcionário. Sai DE VERDADE do estoque do jogador; o
   * servidor devolve 409 se faltar quantidade ou se houver outra isca (retire
   * antes com removeBait).
   */
  async addBait({ commit, rootState }, { employeeId, bait, quantity }) {
    try {
      const playerId = rootState.player?.id;
      const equipamento = await v2.entregarIsca(
        playerId,
        employeeId,
        bait.baseId || bait.id, // id de catálogo da isca
        quantity
      );

      const equipment = equipamentoParaStore(equipamento);
      commit('SET_EMPLOYEE_EQUIPMENT', { employeeId, equipment });

      return { success: true, employee: { id: employeeId, equipment } };
    } catch (error) {
      console.error('Error adding bait to employee:', error);
      throw new Error(mensagemDeErro(error));
    }
  },

  // Retira a isca do funcionário e devolve o restante ao estoque do jogador
  async removeBait({ commit, rootState }, employeeId) {
    try {
      const playerId = rootState.player?.id;
      const equipamento = await v2.retirarIsca(playerId, employeeId);

      const equipment = equipamentoParaStore(equipamento);
      commit('SET_EMPLOYEE_EQUIPMENT', { employeeId, equipment });

      return { success: true, employee: { id: employeeId, equipment } };
    } catch (error) {
      console.error('Error removing bait from employee:', error);
      throw new Error(mensagemDeErro(error));
    }
  },

  /**
   * Níveis E custos de upgrade numa chamada só — o V2 devolve os dois juntos.
   * Os custos ficam cacheados para `getUpgradeCost` não ir de novo à rede.
   */
  async getEmployeeUpgrades({ commit, rootState }, employeeId) {
    try {
      const playerId = rootState.player?.id;
      const { stats, costs, gold } = await v2.upgradesDoFuncionario(playerId, employeeId);

      commit('SET_EMPLOYEE_UPGRADES', { employeeId, upgrades: stats });
      commit('SET_EMPLOYEE_UPGRADE_COSTS', { employeeId, costs });

      return { success: true, upgrades: stats, costs, gold };
    } catch (error) {
      console.error('Error getting employee upgrades:', error);
      throw new Error(mensagemDeErro(error));
    }
  },

  /**
   * Custo de subir um atributo — do SERVIDOR (cacheado da chamada de upgrades;
   * se ainda não tiver, busca). O cálculo local saiu: já divergiu do backend.
   */
  async getUpgradeCost({ state, dispatch }, { employeeId, stat }) {
    try {
      let costs = state.employeeUpgradeCosts[employeeId];
      if (!costs || costs[stat] === undefined) {
        const resultado = await dispatch('getEmployeeUpgrades', employeeId);
        costs = resultado.costs;
      }
      return { success: true, cost: costs?.[stat] ?? 0 };
    } catch (error) {
      console.error(`Error getting upgrade cost for ${stat}:`, error);
      throw new Error(mensagemDeErro(error));
    }
  },

  /**
   * Aplica um upgrade. O custo é debitado do GOLD DO JOGADOR no servidor —
   * nenhum débito local; o saldo que vale é o que a resposta trouxe.
   */
  async upgradeStat({ commit, rootState }, { employeeId, stat }) {
    try {
      const playerId = rootState.player?.id;
      const resultado = await v2.aplicarUpgradeDoFuncionario(playerId, employeeId, stat);

      // Nível novo do atributo
      commit('UPDATE_EMPLOYEE_UPGRADES', {
        employeeId,
        upgrades: { [stat]: resultado.level }
      });

      // O custo do PRÓXIMO nível já veio na resposta — atualiza o cache
      commit('UPDATE_EMPLOYEE_UPGRADE_COST', {
        employeeId,
        stat,
        cost: resultado.nextCost
      });

      // Gold do servidor, nunca conta local
      commit('player/SET_GOLD', resultado.gold, { root: true });

      return {
        success: true,
        newLevel: resultado.level,
        // O V2 não devolve os stats derivados nesta rota; quem precisar deles
        // recarrega o funcionário (loadEmployees / loadBusinessData).
        newStats: null,
        remainingGold: resultado.gold
      };
    } catch (error) {
      console.error(`Error upgrading ${stat}:`, error);
      throw new Error(mensagemDeErro(error));
    }
  }
};

const getters = {
  getEmployees: state => state.employees,
  getSelectedEmployee: state => state.selectedEmployee,
  getEmployeeEquipment: state => employeeId => {
    const equipment = state.employeeEquipment[employeeId];
    if (!equipment) return { rod: null, boat: null, bait: null };

    return equipment;
  },
  getEmployeeUpgrades: state => employeeId => {
    return state.employeeUpgrades[employeeId] || null;
  },
  getEmployeeUpgradeCosts: state => employeeId => {
    return state.employeeUpgradeCosts[employeeId] || null;
  }
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
